import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Input,
  Table,
  Typography,
  Button,
  FormControl,
  FormLabel,
  Card,
  Alert,
  Chip,
  Select,
  Option
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';
import { BRAND } from '../brand';
import EquipmentPopup from '../components/EquipmentPopup';
import AddEquipmentModal from '../components/AddEquipmentModal';
import StockListCard from '../components/StockListCard';
import StockMobileList from '../components/StockMobileList';
import StockPageLayout from '../components/StockPageLayout';
import { useSetPageHeaderAction } from '../context/PageHeaderActionsContext';
import StockAddHeaderButton from '../components/StockAddHeaderButton';
import useIsMobile from '../hooks/useIsMobile';

const STATUS_LABELS = {
  purchased: 'Purchased',
  installed: 'Installed',
  in_service: 'In Service',
  broken: 'Broken',
  scraped: 'Scraped'
};

const STATUS_COLORS = {
  purchased: 'neutral',
  installed: 'success',
  in_service: 'warning',
  broken: 'danger',
  scraped: 'neutral'
};

const Equipment = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const fetchEquipment = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      const response = await axiosInstance.get('/equipment', { params });
      setEquipment(response.data.equipment || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch equipment');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  const headerAction = useMemo(
    () => (
      <StockAddHeaderButton
        label="Add equipment"
        onClick={() => setAddModalOpen(true)}
      />
    ),
    []
  );

  useSetPageHeaderAction(headerAction);

  const handleEquipmentClick = (id) => {
    if (isMobile) {
      navigate(`/equipment/${id}`);
      return;
    }
    setSelectedId(id);
  };

  const totalPages = Math.ceil(equipment.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated = equipment.slice(startIndex, startIndex + itemsPerPage);

  const paginationBar = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      <Typography level="body-md">
        Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, equipment.length)} of {equipment.length} equipment
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => p - 1)}
          sx={{
            borderColor: 'primary.500',
            color: 'primary.500',
            '&:hover': { borderColor: 'primary.600', backgroundColor: 'rgba(29, 27, 77, 0.1)' }
          }}
        >
          Previous
        </Button>
        <Button
          variant="outlined"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => setCurrentPage((p) => p + 1)}
          sx={{
            borderColor: 'primary.500',
            color: 'primary.500',
            '&:hover': { borderColor: 'primary.600', backgroundColor: 'rgba(29, 27, 77, 0.1)' }
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
    <StockPageLayout
      error={error && <Alert color="danger">{error}</Alert>}
      loading={loading}
      search={(
        <Card sx={{ mb: { xs: 0, md: 3 }, p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <FormLabel>Search</FormLabel>
            <Input
              placeholder="Name, serial, brand..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </FormControl>
          <FormControl sx={{ minWidth: 160 }}>
            <FormLabel>Status</FormLabel>
            <Select value={statusFilter} onChange={(_, v) => setStatusFilter(v || '')} placeholder="All">
              <Option value="">All</Option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <Option key={k} value={k}>{v}</Option>
              ))}
            </Select>
          </FormControl>
        </Card>
      )}
      pagination={paginationBar}
    >
          <StockMobileList empty={paginated.length === 0} emptyMessage="No equipment found">
            {paginated.map((item) => (
              <StockListCard
                key={item.id}
                title={item.equipment_name}
                subtitle={item.brand_make || undefined}
                onClick={() => handleEquipmentClick(item.id)}
                fields={[
                  { label: 'Serial', value: item.serial_no || '—' },
                  {
                    label: 'Status',
                    value: (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Chip size="sm" color={STATUS_COLORS[item.status] || 'neutral'} variant="soft">
                          {STATUS_LABELS[item.status] || item.status}
                        </Chip>
                        {item.has_open_service && (
                          <Chip size="sm" color="warning" variant="outlined">Service open</Chip>
                        )}
                      </Box>
                    )
                  },
                  {
                    label: 'Coverage',
                    value: (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                        {item.under_warranty && <Chip size="sm" color="primary" variant="soft">Warranty</Chip>}
                        {item.amc_active && <Chip size="sm" color="success" variant="soft">AMC Active</Chip>}
                        {item.uncovered && <Chip size="sm" color="neutral" variant="soft">Uncovered</Chip>}
                        {!item.under_warranty && !item.amc_active && !item.uncovered && '—'}
                      </Box>
                    )
                  },
                  {
                    label: 'Warranty Until',
                    value: item.warranty_until
                      ? new Date(item.warranty_until).toLocaleDateString()
                      : '—'
                  },
                  { label: 'Vendor', value: item.vendor_name || '—' },
                  { label: 'Location', value: item.location || '—' }
                ]}
              />
            ))}
          </StockMobileList>

          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              overflowX: 'auto'
            }}
          >
          <Table
            stickyHeader
            hoverRow
            sx={{
              '& thead th': { backgroundColor: 'primary.500', color: 'white' },
              '& tbody tr:nth-of-type(even)': { backgroundColor: 'background.level1' }
            }}
          >
            <thead>
              <tr>
                <th>Name</th>
                <th>Serial</th>
                <th>Status</th>
                <th>Coverage</th>
                <th>Warranty Until</th>
                <th>Vendor</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>No equipment found</td>
                </tr>
              ) : (
                paginated.map((item) => (
                  <tr key={item.id} onClick={() => handleEquipmentClick(item.id)} style={{ cursor: 'pointer' }}>
                    <td>
                      <Typography sx={{ color: BRAND.colors.navy, fontWeight: 600 }}>
                        {item.equipment_name}
                      </Typography>
                      {item.brand_make && (
                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{item.brand_make}</Typography>
                      )}
                    </td>
                    <td>{item.serial_no || '—'}</td>
                    <td>
                      <Chip size="sm" color={STATUS_COLORS[item.status] || 'neutral'} variant="soft">
                        {STATUS_LABELS[item.status] || item.status}
                      </Chip>
                      {item.has_open_service && (
                        <Chip size="sm" color="warning" variant="outlined" sx={{ ml: 0.5 }}>Service open</Chip>
                      )}
                    </td>
                    <td>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {item.under_warranty && <Chip size="sm" color="primary" variant="soft">Warranty</Chip>}
                        {item.amc_active && <Chip size="sm" color="success" variant="soft">AMC Active</Chip>}
                        {item.uncovered && <Chip size="sm" color="neutral" variant="soft">Uncovered</Chip>}
                      </Box>
                    </td>
                    <td>
                      {item.warranty_until
                        ? new Date(item.warranty_until).toLocaleDateString()
                        : '—'}
                    </td>
                    <td>{item.vendor_name || '—'}</td>
                    <td>{item.location || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          </Box>
    </StockPageLayout>

      {selectedId && (
        <EquipmentPopup
          equipmentId={selectedId}
          onClose={() => {
            setSelectedId(null);
            fetchEquipment();
          }}
        />
      )}

      <AddEquipmentModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={fetchEquipment}
      />
    </>
  );
};

export default Equipment;
